import {
  Avatar,
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Drawer,
  Dropdown,
  Flex,
  Input,
  message,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { ItemType } from "antd/es/menu/interface";
import { ColumnsType } from "antd/es/table";
import { ApiService } from "api/api.service";
import CustomModal from "components/CustomStrategy/CustomModal";
import PageTitle from "components/PageTitle";
import TextNumeric from "components/TextNumeric";
import dayjs from "dayjs";
import { APPLICATION_STATUS, ApplicationSchemaDto } from "dtos/application.dto";
import { RoleTypes } from "dtos/user.dto";
import DashboardLayout from "layout/DashboardLayout";
import { useEffect, useMemo, useRef, useState } from "react";
import { LuListFilter } from "react-icons/lu";
import {
  TbAffiliate,
  TbArchive,
  TbArrowsUpDown,
  TbChevronLeft,
  TbChevronRight,
  TbClipboardCheck,
  TbColumns3,
  TbCopy,
  TbDots,
  TbEye,
  TbEyeDollar,
  TbFilter,
  TbInfoCircle,
  TbInfoTriangle,
  TbLoader2,
  TbPlus,
  TbSearch,
  TbSignature,
  TbSortAscendingNumbers,
  TbSortDescendingNumbers,
  TbX,
} from "react-icons/tb";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAppSelector } from "store";
import { formatDateTime, formatMoney } from "utils/functions";
import { queryClient } from "utils/queryClient";
import "./ApplicationAll.module.css";
import { useDebounce } from "hooks/use-debounce";

export type PopulatedApplicationDto = NonNullable<
  ReturnType<typeof ApiService.application.useGetPaginated>["data"]
>["result"]["data"][number];

type T_Params = Parameters<typeof ApiService.application.useGetPaginated>[0];
type T_Filter = T_Params["filter"];

// ---------- URL <-> params persistence ----------
// Every list control (pagination, search, drawer filters, org filter, sort, archived)
// lives in the URL so it survives refreshes and can be shared as a link.

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 20;
const DEFAULT_SORT = { createdAt: -1 } as T_Params["sort"];
const SORT_NONE = "none"; // explicit "no sort" so a cleared sort doesn't fall back to the default after refresh

const URL_KEYS = {
  page: "page",
  limit: "limit",
  search: "search",
  archived: "archived",
  lastStep: "last_step",
  affiliate: "affiliate",
  organisation: "org",
  createdFrom: "created_from",
  createdTo: "created_to",
  sort: "sort",
  order: "order",
} as const;

function readParamsFromUrl(searchParams: URLSearchParams): T_Params {
  const page = Number.parseInt(searchParams.get(URL_KEYS.page) ?? "", 10);
  const limit = Number.parseInt(searchParams.get(URL_KEYS.limit) ?? "", 10);

  const filter = {} as NonNullable<T_Filter>;
  const lastStep = searchParams.getAll(URL_KEYS.lastStep);
  if (lastStep.length > 0) filter.last_step = lastStep as NonNullable<T_Filter>["last_step"];
  const affiliate = searchParams.getAll(URL_KEYS.affiliate);
  if (affiliate.length > 0) filter.affiliate = affiliate;
  const organisation = searchParams.getAll(URL_KEYS.organisation);
  if (organisation.length > 0) filter.organisation = organisation;
  const startDate = searchParams.get(URL_KEYS.createdFrom) ?? undefined;
  const endDate = searchParams.get(URL_KEYS.createdTo) ?? undefined;
  if (startDate || endDate) filter.createdAt = { startDate, endDate };

  const sortKey = searchParams.get(URL_KEYS.sort);
  let sort: T_Params["sort"];
  if (sortKey === SORT_NONE) sort = undefined;
  else if (sortKey) sort = { [sortKey]: searchParams.get(URL_KEYS.order) === "asc" ? 1 : -1 } as T_Params["sort"];
  else sort = DEFAULT_SORT;

  return {
    page: Number.isNaN(page) || page < 1 ? DEFAULT_PAGE : page,
    limit: Number.isNaN(limit) || limit < 1 ? DEFAULT_LIMIT : limit,
    search: searchParams.get(URL_KEYS.search) ?? "",
    archived: searchParams.get(URL_KEYS.archived) === "true",
    filter,
    sort,
  };
}

function writeParamsToUrl(prev: URLSearchParams, params: T_Params): URLSearchParams {
  const next = new URLSearchParams(prev);
  // Clear only the keys we own, so unrelated query params are left alone
  Object.values(URL_KEYS).forEach((key) => next.delete(key));

  next.set(URL_KEYS.page, String(params.page ?? DEFAULT_PAGE));
  next.set(URL_KEYS.limit, String(params.limit ?? DEFAULT_LIMIT));
  if (params.search) next.set(URL_KEYS.search, params.search);
  if (params.archived) next.set(URL_KEYS.archived, "true");

  const filter = params.filter;
  filter?.last_step?.forEach((v) => next.append(URL_KEYS.lastStep, String(v)));
  filter?.affiliate?.forEach((v) => next.append(URL_KEYS.affiliate, String(v)));
  filter?.organisation?.forEach((v) => next.append(URL_KEYS.organisation, String(v)));
  if (filter?.createdAt?.startDate) next.set(URL_KEYS.createdFrom, filter.createdAt.startDate);
  if (filter?.createdAt?.endDate) next.set(URL_KEYS.createdTo, filter.createdAt.endDate);

  const sortKey = params.sort ? Object.keys(params.sort)[0] : undefined;
  if (sortKey) {
    const order = (params.sort as Record<string, number>)[sortKey];
    next.set(URL_KEYS.sort, sortKey);
    next.set(URL_KEYS.order, order === 1 ? "asc" : "desc");
  } else {
    next.set(URL_KEYS.sort, SORT_NONE);
  }

  return next;
}

function ApplicationsAll() {
  const currentUser = useAppSelector((state) => state.user);
  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<string | undefined>();
  const archiveApplication = ApiService.application.useArchiveApplication();
  // Hydrate everything from the URL once on mount
  const [params, setParams] = useState<T_Params>(() => readParamsFromUrl(searchParams));
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const orgs = ApiService.organisation.useGetAllOrganisations({ allowed: true });
  const [selectedOrgs, setSelectedOrgs] = useState<string[]>(() => searchParams.getAll(URL_KEYS.organisation));
  const applications = ApiService.application.useGetPaginated(params);
  const [showColumns, setShowColumns] = useState<Partial<Record<keyof ApplicationSchemaDto, boolean>>>({
    affiliate: true,
    "lead.name": true,
    "lead.email": true,
    "lead.debt_amount": true,
    last_step: true,
    "affiliate.name": true,
    createdAt: true,
    actions: true,
  } as any);

  // Single source of truth: whenever params change, mirror them into the URL.
  // `replace` avoids a history entry per keystroke/filter tweak.
  useEffect(() => {
    setSearchParams((prev) => writeParamsToUrl(prev, params), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // True only when the org selection was changed by the user (not on initial setup / hydration).
  // Lets the debounced effect below reset pagination for real user changes while preserving
  // the page from the URL on first load / refresh.
  const orgFilterChangedByUser = useRef(false);

  const handleOrgFilterChange = (value: string[]) => {
    orgFilterChangedByUser.current = true;
    setSelectedOrgs(value);
  };

  const debouncedSelectedOrgs = useDebounce(selectedOrgs, 1000);
  useEffect(() => {
    // Read and clear the flag outside the state updater (updaters can run twice in StrictMode)
    const shouldResetPage = orgFilterChangedByUser.current;
    orgFilterChangedByUser.current = false;

    setParams((prev) => ({
      ...prev,
      ...(shouldResetPage ? { page: 1 } : {}),
      filter: {
        ...prev.filter,
        organisation: debouncedSelectedOrgs,
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSelectedOrgs]);

  useEffect(() => {
    const numberOfOrgs = new Set(orgs.data?.map((d) => d?._id).filter(Boolean)).size;
    setShowColumns((prev) => ({
      ...prev,
      organisation: numberOfOrgs > 1,
    }));
  }, [orgs.data]);

  useEffect(() => {
    if (currentUser?.role === RoleTypes.SUPERADMIN) {
      setShowColumns((prev) => ({ ...prev, organisation: true }));
    }
  }, [currentUser?.role]);

  const sortOrder = params?.sort && Object.values(params?.sort)?.length > 0 ? Object.values(params?.sort)[0] : -1;

  const columns: ColumnsType<PopulatedApplicationDto> & { name: string }[] = [
    {
      key: "lead.name",
      title: "Name",
      name: "Name",
      render: (_, record) => {
        const name = record?.meta?.business_name || record?.meta?.client_name || record?.lead?.name || record?.app_key;

        return (
          <div className="flex min-w-0 max-w-full items-center gap-2">
            <Tooltip title={name}>
              <Link to={`/dashboard/application/${record?.app_key}`} className="min-w-0 max-w-[200px] truncate">
                {name}
              </Link>
            </Tooltip>

            <div className="flex shrink-0 items-center gap-1.5">
              {record?.source_uri?.toLowerCase()?.includes("dev") ? <Tag className="m-0">DEV</Tag> : null}

              {record?.approval_requested === true ? (
                <Tooltip title="Manager's approval requested">
                  <TbInfoCircle color="red" className="size-4 shrink-0" />
                </Tooltip>
              ) : null}

              {record?.isPriorApplication === true ? (
                <Tooltip title="Prior Application">
                  <TbInfoTriangle className="size-4 shrink-0 stroke-rose-600" />
                </Tooltip>
              ) : null}

              {record?.last_step === "signatures" ? (
                <Tooltip title="PandaDoc complete">
                  <TbSignature className="size-4 shrink-0 stroke-green-600" />
                </Tooltip>
              ) : null}
            </div>
          </div>
        );
      },
    },
    {
      key: "lead.email",
      title: "Email",
      name: "Email",
      render: (_, record) => {
        return <Typography.Text>{record?.meta?.email ? record?.meta?.email : record?.lead?.email}</Typography.Text>;
      },
    },
    {
      key: "organisation",
      name: "Organisation",
      title: (
        <Flex className="w-full justify-between">
          <Typography.Text>Organisation</Typography.Text>
          <Select
            mode="multiple"
            size="small"
            variant="borderless"
            suffixIcon={<TbFilter className="text-gray-400" />}
            maxTagCount={0}
            options={orgs.data?.map((d) => ({
              label: (
                <Flex gap={8} className="items-center min-w-32">
                  <Avatar
                    size={20}
                    className="min-w-5 h-5"
                    style={{
                      fontSize: 10,
                    }}
                    src={d.meta?.favicon}
                  >
                    {d.code.at(0)?.toLocaleUpperCase()}
                  </Avatar>
                  <Typography.Text className="w-3/4" ellipsis>
                    {d.name}
                  </Typography.Text>
                </Flex>
              ),
              value: d._id,
            }))}
            value={selectedOrgs}
            onChange={handleOrgFilterChange}
            popupMatchSelectWidth={false}
            tagRender={() => <div />}
            className="filter-select-trigger !cursor-pointer"
          />
        </Flex>
      ),
      render: (_, record) => {
        return (
          <Typography.Text>
            {record?.organisation?.code ? (
              <div className="flex">
                <div className="inline-flex items-center gap-1 p-2 border border-r-0 border-solid border-gray-200 rounded-l-full h-8">
                  <img src={record?.organisation?.meta?.favicon} className="size-5 rounded-full" />
                  <Typography.Text className="text-xs">{record?.organisation?.meta?.company_name}</Typography.Text>
                </div>
                <div className="inline-flex items-center p-2 bg-gray-50 border border-solid border-gray-200 rounded-r-full h-8">
                  <Typography.Text
                    className="text-xs font-semibold"
                    type="secondary"
                    copyable={{
                      text: record?.organisation?.code,
                      icon: [
                        <TbCopy key="copy-icon" className="flex-shrink-0" />,
                        <TbClipboardCheck key="copied-icon" className="flex-shrink-0" />,
                      ],
                      tooltips: ["Copy", "Copied"],
                    }}
                  >
                    {record?.organisation?.code?.toUpperCase()}
                  </Typography.Text>
                </div>
              </div>
            ) : (
              "-"
            )}
          </Typography.Text>
        );
      },
    },
    {
      key: "lead.debt_amount",
      title: "Debt Amount",
      name: "Debt Amount",
      align: "right",
      width: 130,
      render: (_, record) => {
        return (
          <TextNumeric>
            <Typography.Text>
              {record?.enrolled_debt
                ? formatMoney(record?.enrolled_debt)
                : record?.lead?.debt_amount
                  ? formatMoney(record?.lead?.debt_amount)
                  : "-"}
            </Typography.Text>
          </TextNumeric>
        );
      },
    },
    // {
    //   key: "last_step",
    //   title: "Last Step",
    //   name: "Last Step",
    //   render: (_, record) => {
    //     let color = "orange";
    //     if (record.last_step === "onboarding") color = "green-inverse";
    //     else if (record.last_step === "signatures") color = "blue";
    //     if (record?.last_step) {
    //       return <Tag color={color}>{record?.last_step?.split("_")?.join(" ")?.toUpperCase()}</Tag>;
    //     }
    //     return "-";
    //   },
    // },
    {
      key: "affiliate.name",
      title: "Affiliate",
      name: "Affiliate",
      width: 200,
      render: (_, record) => {
        return <Typography.Text>{record?.affiliate?.name}</Typography.Text>;
      },
    },
    {
      key: "createdAt",
      dataIndex: "createdAt",
      title: "Created at",
      name: "Created at",
      width: 200,
      render: (_, record) => {
        return (
          <TextNumeric>
            <Typography.Text type="secondary">
              <small>{record?.createdAt ? formatDateTime(record?.createdAt) : "-"}</small>
            </Typography.Text>
          </TextNumeric>
        );
      },
    },
    {
      title: "Actions",
      name: "Actions",
      key: "actions",
      fixed: "right",
      width: 80,
      render: (_, record) => {
        const menuItems: ItemType[] = [
          {
            key: "archive",
            icon: <TbArchive />,
            label: record.archived === undefined ? "Archive" : record.archived ? "Unarchive" : "Archive",
            onClick: () => {
              archiveApplication.mutate(
                { id: record._id },
                {
                  onSuccess: (data) => {
                    message.success(data.data.message);
                    queryClient.invalidateQueries({ queryKey: ["application"] });
                  },
                },
              );
            },
          },
          // {
          //   key: "affiliate-code",
          //   icon: <TbAffiliate />,
          //   disabled: record.affiliate !== undefined,
          //   label: "Add affiliate code",
          //   onClick: () => {},
          // },
          {
            key: "custom-strategy",
            icon: <TbEyeDollar />,
            label: "View custom strategy",
            onClick: () => {
              setSelectedApplication(record.app_key);
              setIsOpen((prev) => !prev);
            },
          },
        ];

        return (
          <>
            <Dropdown
              menu={{
                items: menuItems,
              }}
            >
              <Button icon={<TbDots />} />
            </Dropdown>
          </>
        );
      },
    },
  ];

  return (
    <DashboardLayout
      headerRight={
        <Space.Compact>
          <Button
            onClick={() => navigate(`/dashboard/create-application?mode=standard`)}
            type="primary"
            icon={<TbPlus />}
          >
            Create New Application
          </Button>
          <Dropdown
            menu={{
              items: [
                {
                  key: "percentage-mode",
                  label: "Create with % mode",
                  onClick: () => {
                    navigate(`/dashboard/create-application?mode=percentage`);
                  },
                },
              ],
            }}
          >
            <Button type="primary" icon={<TbDots />}></Button>
          </Dropdown>
        </Space.Compact>
      }
    >
      {isOpen && <CustomModal isOpen={isOpen} onClose={() => setIsOpen(false)} appkey={selectedApplication} />}
      <PageTitle pageTitle={[{ title: "Applications" }]} title="Applications" />
      <div className="flex justify-between gap-3 p-3">
        <div className="inline-flex gap-3 items-center">
          <Dropdown
            trigger={["click"]}
            menu={{
              items: (columns as { key: string; name: string }[]).map((el) => {
                const key = String(el.key) as keyof typeof showColumns;
                return {
                  key: key,
                  label: (
                    <div className="flex gap-2" key={el.key}>
                      <Checkbox checked={showColumns[key]} />
                      <span>{el.name?.toString()}</span>
                    </div>
                  ),
                };
              }),
              onClick: ({ key }) => {
                setShowColumns((prev) => {
                  return { ...prev, [key]: !(prev as any)[key] };
                });
              },
            }}
          >
            <Button icon={<TbColumns3 />}>
              Columns{" "}
              <Tag bordered={false} className="!m-0 !bg-gray-100 !font-medium">
                {
                  columns.filter((el) => {
                    const key = String(el.key) as keyof typeof showColumns;
                    return showColumns[key];
                  }).length
                }
              </Tag>
            </Button>
          </Dropdown>
          <Button
            icon={<LuListFilter />}
            onClick={() => {
              setIsFilterOpen(true);
            }}
          >
            Filter{" "}
            {params.filter ? (
              <Tag bordered={false} className="!m-0 !bg-gray-100 !font-medium">
                {
                  Object.values(params.filter)
                    .map((el) => el)
                    .flat()?.length
                }
              </Tag>
            ) : null}
          </Button>
          <Space.Compact>
            <Dropdown
              trigger={["click"]}
              menu={{
                selectable: true,
                selectedKeys: params?.sort ? Object.keys(params.sort) : [],
                onSelect: ({ key }) => setParams((prev) => ({ ...prev, page: 1, sort: { [key]: sortOrder } as any })),
                items: [
                  // {
                  //   key: "name",
                  //   label: "Name",
                  // },
                  {
                    key: "createdAt",
                    label: "Created at",
                  },
                  // {
                  //   key: "lead.debt_amount",
                  //   label: "Debt Amount",
                  // },
                ],
              }}
            >
              <Button
                className="border-gray-300"
                variant={params?.sort ? "filled" : "outlined"}
                color={params?.sort ? "primary" : "default"}
                icon={<TbArrowsUpDown />}
              >
                Sort
              </Button>
            </Dropdown>
            {params?.sort && Object.keys(params?.sort)?.length > 0 ? (
              <>
                <Button
                  color="default"
                  className="border-gray-300 px-2"
                  icon={sortOrder === -1 ? <TbSortDescendingNumbers /> : <TbSortAscendingNumbers />}
                  onClick={() =>
                    setParams((prev) => {
                      if (!prev?.sort) return prev;
                      const key = Object.keys(prev?.sort)?.[0];
                      return { ...prev, page: 1, sort: { [key]: sortOrder === 1 ? -1 : 1 } };
                    })
                  }
                >
                  <Typography.Text className="text-xs w-10">{sortOrder === -1 ? "DESC" : "ASC"}</Typography.Text>
                </Button>
                <Button icon={<TbX />} onClick={() => setParams((prev) => ({ ...prev, page: 1, sort: undefined }))} />
              </>
            ) : null}
          </Space.Compact>
          <Space.Compact>
            <Button
              icon={<TbArchive />}
              type={!params.archived ? "default" : "primary"}
              onClick={() => {
                setParams((prev) => ({ ...prev, page: 1, archived: !prev.archived }));
              }}
            >
              {!params.archived ? "Show Archived" : "Hide Archived"}
            </Button>
          </Space.Compact>
        </div>
        <div className="inline-flex gap-3 items-center">
          <Input
            prefix={<TbSearch />}
            defaultValue={params.search}
            placeholder="Search applications..."
            onPressEnter={(e) => {
              const value = e.currentTarget.value;
              setParams((prev) => ({ ...prev, search: value, page: 1 }));
            }}
            allowClear
            onClear={() => {
              setParams((prev) => ({ ...prev, search: undefined, page: 1 }));
            }}
          />
        </div>
      </div>
      <Table<PopulatedApplicationDto>
        scroll={{ x: "max-content", y: "calc(100vh - 202px)" }}
        virtual={false}
        size="small"
        columns={columns.filter((el) => {
          const key = String(el.key) as keyof typeof showColumns;
          // if (key === "organisation" && currentUser?.role !== RoleTypes.SUPERADMIN) return false;
          return key in showColumns && showColumns[key] == true;
        })}
        loading={{
          spinning: applications.isFetching,
          indicator: <TbLoader2 size={48} className="text-brand-500 animate-spin" />,
        }}
        dataSource={applications.data?.result.data}
        rowKey={"_id"}
        pagination={{
          hideOnSinglePage: false,
          showQuickJumper: true,
          showPrevNextJumpers: true,
          nextIcon: (
            <Button
              className="ant-pagination-item-link !flex items-center justify-center"
              type="text"
              icon={<TbChevronRight size={20} />}
            />
          ),
          prevIcon: (
            <Button
              className="ant-pagination-item-link !flex items-center justify-center"
              type="text"
              icon={<TbChevronLeft size={20} />}
            />
          ),
          showTitle: true,
          showTotal: (total, range) => {
            return `Showing ${range[0]}-${range[1]} of ${total} applications`;
          },
          showSizeChanger: true,
          total: applications.data?.result?.totalObjects,
          current: params.page,
          pageSize: params.limit,
          onChange: (page, pageSize) => {
            setParams((prev) => ({ ...prev, page: page, limit: pageSize }));
          },
        }}
      />
      <FilterDrawer
        open={isFilterOpen}
        onClose={(filter) => {
          if (filter) {
            // Keep the org filter (owned by the column header), replace everything the drawer owns
            setParams((prev) => ({
              ...prev,
              page: 1,
              filter: {
                ...filter,
                ...(prev.filter?.organisation ? { organisation: prev.filter.organisation } : {}),
              },
            }));
          }
          setIsFilterOpen(false);
        }}
        existingParams={params}
      />
    </DashboardLayout>
  );
}

function FilterDrawer({
  open,
  onClose,
  existingParams,
}: {
  open: boolean;
  onClose: (filter?: T_Filter) => void;
  existingParams: T_Params;
}) {
  const [filter, setFilter] = useState<T_Filter>(existingParams.filter);
  const paginatedAllUsers = ApiService.user.useGetPaginated({ page: 1, limit: 100, role: [] });

  // Re-sync with the applied filters every time the drawer opens (discards un-applied edits after Cancel)
  useEffect(() => {
    if (open) setFilter(existingParams.filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Drawer
      open={open}
      onClose={() => onClose()}
      footer={
        <div className="flex gap-2">
          <Button
            className="flex-1"
            onClick={() => {
              // Clear every drawer filter and apply right away (org column filter is preserved by the parent)
              setFilter({});
              onClose({});
            }}
          >
            Reset all
          </Button>
          <Button
            className="flex-1"
            variant="solid"
            color="primary"
            onClick={() => {
              onClose(filter);
            }}
          >
            Filter
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 p-4">
        <Typography.Text type="secondary">Last Step</Typography.Text>
        <div className="flex flex-col gap-2 ml-4">
          {Object.entries(APPLICATION_STATUS).map(([key, value]) => {
            return (
              <button
                className="flex gap-2 bg-transparent border-none outline-none cursor-pointer p-0 m-0"
                key={key}
                onClick={() => {
                  setFilter((prev) => {
                    const lastStep = [...(prev?.last_step ?? [])];
                    // Toggle this step while keeping the others selected
                    const newLS = lastStep.includes(value)
                      ? lastStep.filter((el) => el !== value)
                      : [...lastStep, value];
                    return {
                      ...prev,
                      last_step: newLS,
                    };
                  });
                }}
              >
                <Checkbox checked={filter?.last_step?.includes(value)} />
                <Tag>{key.replace(/_/g, " ")}</Tag>
              </button>
            );
          })}
        </div>
        <Divider className="!my-2" />
        <Typography.Text type="secondary">Created at</Typography.Text>
        <div className="flex flex-col gap-2 ml-4" key={"created_at"}>
          <Typography.Text>Start Date</Typography.Text>
          <DatePicker
            value={filter?.createdAt?.startDate ? dayjs(filter?.createdAt?.startDate) : undefined}
            onChange={(date) => {
              setFilter((prev) => ({
                ...prev,
                createdAt: { ...prev?.createdAt, startDate: date ? date.toISOString() : undefined },
              }));
            }}
            disabledDate={(date) => {
              if (filter?.createdAt?.endDate) {
                return dayjs(filter?.createdAt?.endDate).isBefore(date);
              } else return false;
            }}
          />
          <Typography.Text>End Date</Typography.Text>
          <DatePicker
            value={filter?.createdAt?.endDate ? dayjs(filter?.createdAt?.endDate) : undefined}
            onChange={(date) => {
              setFilter((prev) => ({
                ...prev,
                createdAt: { ...prev?.createdAt, endDate: date ? date.toISOString() : undefined },
              }));
            }}
            disabledDate={(date) => {
              if (filter?.createdAt?.startDate) {
                return dayjs(filter?.createdAt?.startDate).isAfter(date);
              } else return false;
            }}
          />
        </div>
        <Divider className="!my-2" />
        <Typography.Text>Affiliates</Typography.Text>
        <Select
          allowClear
          showSearch
          prefix={<TbSearch />}
          placeholder="Search users..."
          mode="multiple"
          value={filter?.affiliate ?? []}
          loading={paginatedAllUsers.isPending}
          options={paginatedAllUsers.data?.result?.data?.map((user) => ({ value: user._id, label: user?.name }))}
          filterOption={(searchTerm, option) => {
            return !!option?.label?.toLowerCase()?.includes(searchTerm?.toLowerCase());
          }}
          onChange={(val) => {
            if (Array.isArray(val) && val?.length > 0) {
              setFilter((prev) => ({ ...prev, affiliate: val }));
            } else {
              setFilter((prev) => {
                const newVal = { ...prev };
                delete newVal.affiliate;
                return newVal;
              });
            }
          }}
          onClear={() => {
            setFilter((prev) => {
              const newVal = { ...prev };
              delete newVal.affiliate;
              return newVal;
            });
          }}
        />
      </div>
    </Drawer>
  );
}

export default ApplicationsAll;
